import { StaticImage } from "gatsby-plugin-image";
import { useEffect, useRef, useState } from "react";
import tw, { styled } from "twin.macro";

import sliderHandleSrc from "../../assets/images/features/slider-handle.svg";

const FlowContainer = styled.div`
  ${tw`relative mb-[70px]`}
`;

const Flow = styled.div`
  ${tw`relative rounded-[50px] overflow-hidden`}
  box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);

  img {
    max-width: 100%;
  }
`;

const FlowBranded = styled.div`
  ${tw`absolute h-full top-0 left-0`}

  .gatsby-image-wrapper {
    ${tw`h-full`}
  }

  img,
  picture {
    object-position: left top;
    max-width: unset;
  }
`;

const SliderBar = styled.div`
  ${tw`absolute bg-theme-blue-1 rounded-full`}
  width: 8px;
  height: calc(100% + 34px);
  top: -17px;
`;

const SliderHandle = styled.div`
  ${tw`absolute rounded-full cursor-grab`}
  width: 39px;
  height: 39px;
  left: -15.5px;
  top: calc(50% - 14.5px);

  img {
    ${tw`h-full w-full`}
    pointer-events: none;
  }

  @media (pointer: coarse) {
    width: 48px;
    height: 48px;
    left: -20px;
    top: calc(50% - 24px);
    padding: 4.5px;
  }
`;

const SliderGhost = styled.img`
  ${tw`absolute top-0 left-0`}
  pointer-events: none;
`;

const transparentPixel =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

const CustomBrandingSlider: React.FunctionComponent = () => {
  const flowDiv = useRef<HTMLDivElement>(null);
  const sliderGhostImg = useRef<HTMLImageElement>(null);
  const sliderHandleDiv = useRef<HTMLDivElement>(null);
  const [percent, setPercent] = useState(50);

  const setPercentFromPageX = (pageX: number) => {
    const rect = flowDiv.current?.getBoundingClientRect();

    if (rect && pageX > 0) {
      let percent = ((pageX - rect.x) / rect.width) * 100;
      if (percent < 0) {
        percent = 0;
      }
      setPercent(percent > 100 ? 100 : percent);
    }
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (sliderGhostImg.current) {
      e.dataTransfer.setDragImage(sliderGhostImg.current, 0, 0);
    }
  };

  const handleDragEvent = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setPercentFromPageX(e.pageX);
  };

  // Touch-devices
  useEffect(() => {
    const current = sliderHandleDiv.current;

    if (!current) {
      return;
    }

    const handler = (e: TouchEvent) => {
      e.preventDefault();
      setPercentFromPageX(e.targetTouches[0].pageX);
    };

    // Attached the old-fashioned way to avoid being a "passive" event handler, otherwise the page moves, too
    sliderHandleDiv.current.addEventListener("touchmove", handler);

    return function cleanup() {
      current.removeEventListener("touchmove", handler);
    };
  });

  return (
    <>
      <SliderGhost
        ref={sliderGhostImg}
        src={transparentPixel}
        alt=""
        width={1}
        height={1}
      />
      <FlowContainer>
        <Flow ref={flowDiv}>
          <StaticImage
            src="../../assets/images/features/flow-unbranded.png"
            alt=""
            width={1098}
            height={766}
            placeholder="none"
          />
          <FlowBranded style={{ width: `${percent}%` }}>
            <StaticImage
              src="../../assets/images/features/flow-branded.png"
              alt=""
              width={1098}
              height={766}
              placeholder="none"
            />
          </FlowBranded>
        </Flow>
        <SliderBar style={{ left: `calc(${percent}% - 4px)` }}>
          <SliderHandle
            ref={sliderHandleDiv}
            draggable={true}
            onDragStart={handleDragStart}
            onDrag={handleDragEvent}
          >
            <img src={sliderHandleSrc} alt="" width={39} height={39} />
          </SliderHandle>
        </SliderBar>
      </FlowContainer>
    </>
  );
};

export default CustomBrandingSlider;
